import { MoreVertical } from "lucide-react";
import Toast from "../../../../components/Toast";
import { CandidateViewDetails } from "../../../candidate/components/candidateViewDetails";
import { ApplicationStatus } from "../model/jobEnums";
import { hideToast, setOpenMenu } from "../viewmodel/job.action";

interface Props {
  state: any;
  dispatch: React.Dispatch<any>;
  handleViewContact: (candidateId: string, applicationId: string) => void;
  refreshApplications: () => void;
  handleChangeApplicationStatus: (
    applicationId: string,
    jobId: string,
    status: string,
  ) => void;
}

export default function ApplicationDetailsView({
  state,
  dispatch,
  handleViewContact,
  handleChangeApplicationStatus,
  refreshApplications,
}: Props) {
  const application = state.selectedApplication;

  if (state.viewLoading) {
    return (
      <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
    );
  }

  if (!application) {
    return (
      <div className="py-10 text-center text-sm text-gray-500">
        Data not found
      </div>
    );
  }
  const APPLICATION_STATUS_TRANSITIONS: Record<string, ApplicationStatus[]> = {
    submitted: [
      ApplicationStatus.IN_REVIEW,
      ApplicationStatus.REJECTED,
    ],
    in_review: [
      ApplicationStatus.SHORTLISTED,
      ApplicationStatus.REJECTED,
      ApplicationStatus.ON_HOLD,
    ],
    shortlisted: [
      ApplicationStatus.INTERVIEW_SCHEDULED,
      ApplicationStatus.REJECTED,
      ApplicationStatus.ON_HOLD,
    ],
    on_hold: [
      ApplicationStatus.IN_REVIEW,
      ApplicationStatus.SHORTLISTED,
      ApplicationStatus.REJECTED,
    ],
    interview_scheduled: [
      ApplicationStatus.INTERVIEWED,
      ApplicationStatus.REJECTED,
    ],
    interviewed: [
      ApplicationStatus.INTERVIEW_SCHEDULED,
      ApplicationStatus.OFFERED,
      ApplicationStatus.REJECTED,
    ],
    offered: [
      ApplicationStatus.HIRED,
      ApplicationStatus.DECLINED,
    ],
    rejected: [],
    hired: [],
    declined: [],
    withdrawn: [],
  };
  const statusLabelMap: Record<ApplicationStatus, string> = {
    [ApplicationStatus.IN_REVIEW]: "Under Review",
    [ApplicationStatus.ON_HOLD]: "On Hold",
    [ApplicationStatus.SHORTLISTED]: "Shortlisted",
    [ApplicationStatus.INTERVIEW_SCHEDULED]: "Interview Scheduled",
    [ApplicationStatus.INTERVIEWED]: "Interview Completed",
    [ApplicationStatus.OFFERED]: "Offer Extended",
    [ApplicationStatus.DECLINED]: "Offer Declined",
    [ApplicationStatus.HIRED]: "Hired",
    [ApplicationStatus.REJECTED]: "Rejected",
    [ApplicationStatus.WITHDRAWN]: "Withdrawn",
  };
  const currentStatus = application.status;
  const allowedStatuses = APPLICATION_STATUS_TRANSITIONS[currentStatus] || [];
  if (state.candidateViewLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col bg-white rounded-lg  h-[80vh] overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">
          {/* Application Details */}
          {application.candidate.current_job_title || "Application Details"}
        </h3>

        {/* Status Menu */}
        <div className="flex items-center gap-2">
          {application.contact_unlocked && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    setOpenMenu(
                      state.openMenuJobId === application.id
                        ? null
                        : application.id,
                    ),
                  );
                }}
                className="p-2 hover:bg-gray-100 rounded-full text-black"
              >
                <MoreVertical className="w-6 h-6 stroke-[2.5]" />
              </button>

              {state.openMenuJobId === application.id && (
                <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg py-2 z-20 max-h-60 overflow-y-auto">
                  {allowedStatuses.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-400">
                      No actions available
                    </div>
                  ) : (
                    allowedStatuses.map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          handleChangeApplicationStatus(
                            application.id, 
                            application.job_id, 
                            status, 
                          );
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        {statusLabelMap[status]}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => {
              dispatch({ type: "OPEN_APPLICATION_VIEW", payload: false });
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      <CandidateViewDetails
        candidate={application.candidate}
        contactUnlocked={application.contact_unlocked}
        onViewContact={() => {
          handleViewContact(application.candidate_id, application.id);
          refreshApplications();
        }}
      />
    </div>
  );
}
